import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useStateContext, ACCENT_OPTIONS } from "../../contexts/ContextProvider.jsx";
import useAxiosClient from "../../axios-client.js";
import Loading from "../Util/Loading.jsx";
import { toast } from "react-toastify";
import WhatsNewModal from "../WhatsNew/WhatsNewModal";
// The sidebar's structure and its active/expanded rules, kept separate so they
// can be tested without booting the shell.
import { SECTIONS, capitalize, navState, pageTitleFor } from "./navState";

// Design tokens ported from the "App Shell Redesign" mock, mapped to the app's
// two themes. Set as CSS variables on the shell root so both inline styles and
// the <style> hover rules can read them via var(--x). They live in shellTokens
// so screens with a nested ConfigProvider can reuse the same hex values for
// antd's portalled surfaces, which CSS variables cannot reach.
import { PRIMARY, SHELL_THEME as THEME, ringOf } from "../../shellTokens";
import { initialsOf, logoSrc, withFallback } from "../../utils/company";
import NotificationDropdown from "../NotificationDropdown";

// Nav SVG paths (two sub-paths each), lifted from the mock's ICONS map.
const ICONS = {
  dashboard: ["M3 3h7.2v6.6H3zM13.8 3H21v4H13.8zM13.8 10.6H21V21h-7.2zM3 13.4h7.2V21H3z", ""],
  merchants: ["M3.2 9 4.8 4h14.4L20.8 9a3 3 0 0 1-5.9.6A3 3 0 0 1 9 9.6 3 3 0 0 1 3.2 9z", "M5 10.6V20h14v-9.4"],
  orders: ["M4 4.8h16v14.4H4z", "M8 9.2h8M8 13h5"],
  customers: ["M15 20.5v-1.2a4 4 0 0 0-4-4H6.4a4 4 0 0 0-4 4v1.2", "M12.4 7.4a3.7 3.7 0 1 1-7.4 0 3.7 3.7 0 0 1 7.4 0M17.2 20.5v-1.2a4 4 0 0 0-3-3.9"],
  stock: ["M20.5 7.3 12 3.2 3.5 7.3 12 11.4z", "M3.5 12.1 12 16.2l8.5-4.1M3.5 16.6 12 20.8l8.5-4.2"],
  // A bolt of cloth on its roll — raw fabric, as against the stacked-boxes
  // "stock" glyph used for finished goods.
  fabric: ["M6.5 3.5h11a3 3 0 0 1 0 6h-11a3 3 0 0 1 0-6z", "M6.5 9.5v8.2a2.8 2.8 0 0 0 2.8 2.8h8.2a3 3 0 0 0 3-3V6.5M6.5 6.5h11"],
  // A truck — fabric arriving against a supplier challan.
  deliveries: ["M2.8 6.6h10.4v8.6H2.8z", "M13.2 9.8h3.6l2.6 2.7v2.7h-6.2zM7.4 17.6a1.9 1.9 0 1 0 0-3.8 1.9 1.9 0 0 0 0 3.8M16.6 17.6a1.9 1.9 0 1 0 0-3.8 1.9 1.9 0 0 0 0 3.8"],
  // A rising trend line — the fabric sales report.
  sales: ["M3.5 20.5h17", "M6.5 16.5v-4M11 16.5v-8M15.5 16.5v-5M20 16.5v-9"],
  // A banknote — money in, as against the "sales" trend line for what was billed.
  payments: ["M2.8 6.2h18.4v11.6H2.8z", "M12 14.1a2.1 2.1 0 1 0 0-4.2 2.1 2.1 0 0 0 0 4.2M6.3 9.4v5.2M17.7 9.4v5.2"],
  users: ["M16.5 20.6v-1.4a4 4 0 0 0-4-4H6.2a4 4 0 0 0-4 4v1.4", "M13 7.3a3.6 3.6 0 1 1-7.2 0 3.6 3.6 0 0 1 7.2 0M21.8 20.6v-1.4a4 4 0 0 0-3-3.9M16.2 3.6a4 4 0 0 1 0 7.6"],
  settings: ["M12 15.1a3.1 3.1 0 1 0 0-6.2 3.1 3.1 0 0 0 0 6.2z", "M19.2 14.4a1.5 1.5 0 0 0 .3 1.7l.1.1a1.9 1.9 0 1 1-2.7 2.7l-.1-.1a1.5 1.5 0 0 0-2.5 1.1v.3a1.9 1.9 0 0 1-3.8 0v-.2a1.5 1.5 0 0 0-2.5-1.1l-.1.1a1.9 1.9 0 1 1-2.7-2.7l.1-.1a1.5 1.5 0 0 0-1.1-2.5h-.3a1.9 1.9 0 0 1 0-3.8h.2a1.5 1.5 0 0 0 1.1-2.5l-.1-.1a1.9 1.9 0 1 1 2.7-2.7l.1.1a1.5 1.5 0 0 0 2.5-1.1V3.5a1.9 1.9 0 0 1 3.8 0v.2a1.5 1.5 0 0 0 2.5 1.1l.1-.1a1.9 1.9 0 1 1 2.7 2.7l-.1.1a1.5 1.5 0 0 0 1.1 2.5h.3a1.9 1.9 0 0 1 0 3.8h-.2a1.5 1.5 0 0 0-1.4.9z"],
};

const DefaultLayout = () => {
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const location = useLocation();

  const currentKey = location.pathname.split("/")[1] || "dashboard";
  const [collapsed, setCollapsed] = useState(false);

  const {
    user,
    token,
    setUser,
    setPermissions,
    setRoles,
    setUnseenReleases,
    setToken,
    permissions,
    unseenReleases,
    darkMode,
    setDarkMode,
    accentColor,
    setAccentColor,
    company,
    setCompany,
  } = useStateContext();

  // The rail's brand. A company that has not been edited still reads
  // "Elegant Tex" with the "ET" monogram, exactly as before.
  const brandName = withFallback(company).name;
  const brandLogo = logoSrc(company);
  const tokenRef = useRef(token);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const fetchUser = useCallback(() => {
    axiosClient
      .post("auth/me")
      .then(({ data }) => {
        setUser(data.user);
        setPermissions(data.permissions);
        setRoles(data.roles);
        setUnseenReleases(data.unseen_releases ?? []);
      })
      .catch((error) => {
        tokenRef.current = null;
        const message =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();
        toast.error(message);
      });
  }, [axiosClient, setRoles, setPermissions, setUser, setUnseenReleases]);

  // The company profile, fetched once per shell mount. Every invoice prints it,
  // so it is loaded here rather than by each screen that might render one. A
  // failure is silent on purpose: the cached copy (or the fallback) still
  // prints, and a toast about branding on top of whatever else just broke helps
  // nobody.
  const fetchCompany = useCallback(() => {
    axiosClient
      .get("/company")
      .then(({ data }) => setCompany(data.data))
      .catch(() => { /* keep the last known profile */ });
  }, [axiosClient, setCompany]);

  useEffect(() => {
    if (tokenRef.current) {
      fetchUser();
      fetchCompany();
    }
  }, [fetchUser, fetchCompany, tokenRef]);

  // Auto-collapse the rail on narrow viewports, matching the old lg breakpoint.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = (e) => setCollapsed(e.matches);
    apply(mq);
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // ADMIN_DASHBOARD only decides whether Overview is company-wide or scoped to
  // your own work — the backend serves both from the same endpoint, so Overview
  // needs no gate here. Reaching the Teams board is the separate right.
  const isAdminDashboard = permissions.includes("ADMIN_DASHBOARD");
  const canViewTeamsDashboard = isAdminDashboard
    || permissions.includes("VIEW_TEAM_DASHBOARD")
    || permissions.includes("VIEW_ALL_ORDERS");

  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileBtnRef = useRef(null);

  if (!token) {
    return <Navigate to="/login" />;
  }

  const confirmLogout = async () => {
    setLoggingOut(true);
    try {
      const response = await axiosClient.post(`/auth/logout`);
      toast.success(response.data.message);
      setUser(null);
      setToken(null);
    } catch (error) {
      setToken(null);
      setUser(null);
    } finally {
      setLoggingOut(false);
    }
  };

  // Dashboard expands into Overview + Teams for anyone who can reach Teams; for
  // everyone else it stays a single row pointing at Overview. It used to expand
  // only for ADMIN_DASHBOARD, which left a team lead with no link to a page the
  // API would have served them.
  const dashboardChildren = canViewTeamsDashboard
    ? [
        { key: "/dashboard", label: "Overview" },
        { key: "/dashboard/teams", label: "Teams" },
      ]
    : null;

  const activePath = location.pathname === "/" ? "/dashboard" : location.pathname;

  const go = (key) => navigate(key.startsWith("/") ? key : "/" + key);

  // Header breadcrumb: shallow routes show a title, deep routes show a trail.
  const segments = location.pathname.split("/").filter(Boolean);
  const isDeep = segments.length >= 2;
  const pageTitle = pageTitleFor(currentKey);

  const rootStyle = {
    ...Object.fromEntries(
      Object.entries(THEME[darkMode ? "dark" : "light"]).map(([k, v]) => [`--${k}`, v])
    ),
    "--primary": accentColor,
    // Follows the live accent, so the order form's focus ring changes with it.
    "--primaryRing": ringOf(accentColor),
    "--fabGutter": "0px",
    "--accent": accentColor,
    fontFamily: "Outfit, system-ui, sans-serif",
    background: "var(--page)",
    height: "100vh",
    padding: "12px 14px 14px",
    display: "flex",
    flexDirection: "column",
    color: "var(--text)",
  };

  const iconBtn = {
    width: 30, height: 30, flexShrink: 0, border: "none", background: "transparent",
    borderRadius: 8, color: "var(--muted)", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  };

  const userLoaded = user && Object.keys(user).length !== 0;

  const renderNavItem = (item) => {
    const { children, hasChildren, active: selfActive, showChildren } = navState(item, {
      currentKey, activePath, collapsed, dashboardChildren,
    });
    const [icon, icon2] = ICONS[item.icon];
    return (
      <React.Fragment key={item.key}>
        <div
          className="et-navitem"
          onClick={() => go(item.key)}
          title={item.label}
          style={{
            position: "relative", display: "flex", alignItems: "center", gap: 11,
            height: 32, padding: collapsed ? "0 8px" : "0 10px", borderRadius: 8, cursor: "pointer",
            justifyContent: collapsed ? "center" : "flex-start",
            background: selfActive ? "var(--activeBg)" : "transparent",
            color: selfActive ? (darkMode ? "var(--activeText)" : PRIMARY) : "var(--muted)",
          }}
        >
          <span
            style={{
              position: "absolute", left: -8, top: 6, bottom: 6, width: 3,
              borderRadius: "0 3px 3px 0", background: PRIMARY,
              display: selfActive ? "block" : "none",
            }}
          />
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth={selfActive ? 1.9 : 1.7} strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0, opacity: selfActive ? 1 : 0.82 }}>
            <path d={icon} />
            {icon2 && <path d={icon2} />}
          </svg>
          {!collapsed && (
            <span style={{
              fontSize: 13, fontWeight: selfActive ? 700 : 500,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {item.label}
            </span>
          )}
          {!collapsed && hasChildren && (
            <span style={{ marginLeft: "auto", fontSize: 9, color: "var(--dim)" }}>▾</span>
          )}
          {collapsed && selfActive && hasChildren && (
            <span style={{
              position: "absolute", right: 6, top: 6, width: 4, height: 4,
              borderRadius: "50%", background: PRIMARY,
            }} />
          )}
        </div>

        {showChildren &&
          children.map((c) => {
            const childActive = c.key === activePath;
            return (
              <div
                key={c.key}
                className="et-navitem"
                onClick={() => go(c.key)}
                style={{
                  display: "flex", alignItems: "center", gap: 10, height: 28,
                  padding: "0 10px 0 21px", marginLeft: 8, borderLeft: "1px solid var(--border)",
                  borderRadius: "0 7px 7px 0", cursor: "pointer",
                  background: childActive ? "var(--activeBg)" : "transparent",
                  color: childActive ? (darkMode ? "var(--activeText)" : PRIMARY) : "var(--muted)",
                }}
              >
                <span style={{
                  width: 4, height: 4, borderRadius: "50%", flexShrink: 0,
                  background: childActive ? PRIMARY : "var(--dim)",
                }} />
                <span style={{ fontSize: 12.5, fontWeight: childActive ? 700 : 500, whiteSpace: "nowrap" }}>
                  {c.label}
                </span>
              </div>
            );
          })}
      </React.Fragment>
    );
  };

  return (
    <div className="et-shell" style={rootStyle}>
      <style>{`
        .et-shell a{color:inherit;text-decoration:none}
        .et-navitem:hover{background:var(--hover)}
        .et-icbtn:hover{background:var(--hover);color:var(--text)}
        .et-crumb:hover{background:var(--hover);color:var(--text)}
        .et-user:hover{background:var(--hover)}
        .et-user-item:hover{background:var(--hover)}
        .et-shell ::-webkit-scrollbar{width:9px;height:9px}
        .et-shell ::-webkit-scrollbar-thumb{background:var(--borderStrong);border-radius:5px}
        .et-shell ::-webkit-scrollbar-track{background:transparent}
        @media (max-width:640px){.et-hide-sm{display:none !important}}
      `}</style>

      <div style={{
        flex: 1, minHeight: 0, display: "flex", border: "1px solid var(--border)",
        borderRadius: 14, overflow: "hidden", background: "var(--bg)",
      }}>
        {/* ---------- Sidebar rail ---------- */}
        <nav style={{
          width: collapsed ? 58 : 218, flexShrink: 0, borderRight: "1px solid var(--border)",
          background: "var(--sidebar)", display: "flex", flexDirection: "column",
          transition: "width .18s ease",
        }}>
          {/* Brand */}
          <div
            onClick={() => navigate("/")}
            style={{
              height: 46, flexShrink: 0, display: "flex", alignItems: "center", gap: 10,
              padding: collapsed ? "0 15px" : "0 13px", borderBottom: "1px solid var(--border)",
              cursor: "pointer",
            }}
          >
            {/* The uploaded logo when there is one; otherwise the monogram on
                the brand tile, which is what this drew before the profile
                existed and still reads "ET" for the default name. */}
            <div style={{
              width: 26, height: 26, borderRadius: 8,
              background: brandLogo ? "rgba(255,255,255,.94)" : "linear-gradient(140deg,#ff4fa3,#ec1c7e 60%,#c1156a)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              boxShadow: "0 2px 8px rgba(236,28,126,.32)", overflow: "hidden",
            }}>
              {brandLogo ? (
                <img src={brandLogo} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }} />
              ) : (
                <span style={{ fontSize: 11, fontWeight: 800, color: "#fff", letterSpacing: ".4px" }}>
                  {initialsOf(brandName)}
                </span>
              )}
            </div>
            {!collapsed && (
              <div style={{
                fontSize: 13, fontWeight: 700, color: "var(--text)",
                letterSpacing: ".2px", whiteSpace: "nowrap",
                overflow: "hidden", textOverflow: "ellipsis",
              }} title={brandName}>
                {brandName}
              </div>
            )}
          </div>

          {/* Nav */}
          <div style={{
            flex: 1, minHeight: 0, overflowY: "auto", padding: "8px 8px 10px",
            display: "flex", flexDirection: "column", gap: 2,
          }}>
            {SECTIONS.map((section) => {
              const visible = section.items.filter((i) => !i.permission || permissions.includes(i.permission));
              if (visible.length === 0) return null;
              return (
                <React.Fragment key={section.heading}>
                  {!collapsed && (
                    <div style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: ".9px", textTransform: "uppercase",
                      color: "var(--dim)", padding: "12px 8px 5px",
                    }}>
                      {section.heading}
                    </div>
                  )}
                  {visible.map(renderNavItem)}
                </React.Fragment>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{
            flexShrink: 0, borderTop: "1px solid var(--border)", padding: 8,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: 8, background: "var(--surface2)",
              border: "1px solid var(--border)", display: "flex", alignItems: "center",
              justifyContent: "center", flexShrink: 0, fontSize: 11, fontWeight: 700,
              color: "var(--muted)", fontFamily: "'JetBrains Mono',monospace",
            }}>
              ET
            </div>
            {!collapsed && (
              <div style={{ fontSize: 11, color: "var(--dim)", whiteSpace: "nowrap", minWidth: 0 }}>
                © {new Date().getFullYear()} Tripzin.inc
              </div>
            )}
          </div>
        </nav>

        {/* ---------- Main pane ---------- */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {/* Compact header */}
          <header style={{
            height: 46, flexShrink: 0, display: "flex", alignItems: "center", gap: 12,
            padding: "0 12px 0 8px", borderBottom: "1px solid var(--border)", background: "var(--header)",
          }}>
            <button
              className="et-icbtn"
              onClick={() => setCollapsed((c) => !c)}
              title="Toggle sidebar"
              style={iconBtn}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.9" strokeLinecap="round">
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>

            {/* Title / breadcrumb */}
            <div style={{ display: "flex", alignItems: "center", gap: 7, flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
              {!isDeep ? (
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", whiteSpace: "nowrap" }}>
                  {pageTitle}
                </span>
              ) : (
                <>
                  <Link to="/" className="et-crumb" title="Home" style={{
                    width: 26, height: 26, borderRadius: 7, display: "flex", alignItems: "center",
                    justifyContent: "center", color: "var(--crumb)", flexShrink: 0,
                  }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 10.4 12 3l9 7.4" />
                      <path d="M5.6 9.4V20h12.8V9.4" />
                    </svg>
                  </Link>
                  {segments.map((seg, i) => {
                    const to = "/" + segments.slice(0, i + 1).join("/");
                    const last = i === segments.length - 1;
                    const isId = /^\d+$/.test(seg);
                    const label = isId ? `#${seg}` : capitalize(seg);
                    return (
                      <React.Fragment key={to}>
                        <span style={{ color: "var(--sep)", fontSize: 12, flexShrink: 0 }}>/</span>
                        <Link
                          to={to}
                          className="et-crumb"
                          style={{
                            fontSize: last ? 13 : 12.5, fontWeight: last ? 700 : 500,
                            color: last ? "var(--text)" : "var(--crumb)", whiteSpace: "nowrap",
                            fontFamily: isId ? "'JetBrains Mono',monospace" : "Outfit,sans-serif",
                            padding: "3px 5px", borderRadius: 6,
                          }}
                        >
                          {label}
                        </Link>
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </div>

            {/* Right controls */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              {/* Theme toggle */}
              <div style={{
                display: "flex", alignItems: "center", gap: 2, padding: 2, borderRadius: 8,
                border: "1px solid var(--border)", background: "var(--surface2)",
              }}>
                <span
                  onClick={() => setDarkMode(false)}
                  title="Light"
                  style={{
                    width: 24, height: 22, borderRadius: 6, display: "flex", alignItems: "center",
                    justifyContent: "center", cursor: "pointer",
                    background: !darkMode ? PRIMARY : "transparent",
                    color: !darkMode ? "#fff" : "var(--dim)",
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round">
                    <path d="M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z" />
                    <path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
                  </svg>
                </span>
                <span
                  onClick={() => setDarkMode(true)}
                  title="Dark"
                  style={{
                    width: 24, height: 22, borderRadius: 6, display: "flex", alignItems: "center",
                    justifyContent: "center", cursor: "pointer",
                    background: darkMode ? PRIMARY : "transparent",
                    color: darkMode ? "#fff" : "var(--dim)",
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
                  </svg>
                </span>
              </div>

              {/* Notifications Dropdown */}
              <NotificationDropdown iconBtn={iconBtn} unseenReleases={unseenReleases} />

              {/* User */}
              {userLoaded && (
                <div style={{ position: "relative" }} ref={profileBtnRef}>
                  <div className="et-user" onClick={() => setProfileOpen(!profileOpen)} style={{
                    display: "flex", alignItems: "center", gap: 8, height: 30, padding: "0 8px 0 3px",
                    borderRadius: 9, cursor: "pointer",
                  }}>
                    {user.image ? (
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}/files/upload/${user.image.id}`}
                        alt=""
                        style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                      />
                    ) : (
                      <div style={{
                        width: 24, height: 24, borderRadius: "50%",
                        background: darkMode
                          ? `linear-gradient(140deg, ${accentColor}, ${accentColor}dd)`
                          : `linear-gradient(140deg, ${accentColor}, ${accentColor}88)`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", fontSize: 10, fontWeight: 700, flexShrink: 0,
                      }}>
                        {user.firstname ? (user.firstname[0] + (user.lastname?.[0] || "")).toUpperCase() : (user.name?.[0]?.toUpperCase() || "U")}
                      </div>
                    )}
                    <div className="et-hide-sm" style={{ lineHeight: 1.2, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", whiteSpace: "nowrap" }}>
                        {user.firstname ? `${user.firstname} ${user.lastname || ""}`.trim() : user.name}
                      </div>
                    </div>
                    <span style={{ fontSize: 8, color: "var(--dim)" }}>▾</span>
                  </div>

                  {/* Dropdown */}
                  {profileOpen && (
                    <>
                      <div
                        onClick={() => setProfileOpen(false)}
                        style={{ position: "fixed", inset: 0, zIndex: 99 }}
                      />
                      <div style={{
                        position: "absolute", top: "100%", right: 0, marginTop: 4, zIndex: 100,
                        background: "var(--surface)", border: "1px solid var(--border)",
                        borderRadius: 12, boxShadow: "0 12px 32px rgba(0,0,0,.35)",
                        overflow: "hidden", minWidth: 180,
                      }}>
                        {/* Accent color picker */}
                        <div style={{ padding: "8px 14px" }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ font: "600 10px Outfit, sans-serif", color: "var(--dim)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 7 }}>Accent</div>
                          <div style={{ display: "flex", gap: 6 }}>
                            {ACCENT_OPTIONS.map((opt) => (
                              <button
                                key={opt.value}
                                onClick={(e) => { e.stopPropagation(); setAccentColor(opt.value); window.location.reload(); }}
                                title={opt.label}
                                style={{
                                  width: 22, height: 22, borderRadius: "50%",
                                  background: opt.value,
                                  border: accentColor === opt.value ? "2px solid var(--text)" : "2px solid transparent",
                                  cursor: "pointer", padding: 0,
                                  boxShadow: accentColor === opt.value ? `0 0 0 2px ${opt.value}44` : "none",
                                }}
                              />
                            ))}
                          </div>
                        </div>
                        <div style={{ height: 1, background: "var(--border)" }} />
                        <div
                          onClick={() => { navigate("/profile"); setProfileOpen(false); }}
                          style={{
                            padding: "9px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
                            font: "600 13px Outfit, sans-serif", color: "var(--text)",
                          }}
                          className="et-user-item"
                        >
                          Profile
                        </div>
                        <div style={{ height: 1, background: "var(--border)" }} />
                        <div
                          onClick={() => { setLogoutOpen(true); setProfileOpen(false); }}
                          style={{
                            padding: "9px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
                            font: "600 13px Outfit, sans-serif", color: "#f87171",
                          }}
                          className="et-user-item"
                        >
                          Logout
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Logout confirm dialog */}
              {logoutOpen && (
                <>
                  <div onClick={() => setLogoutOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,.45)" }} />
                  <div style={{
                    position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
                    zIndex: 1000, background: "var(--surface)", border: "1px solid var(--borderStrong)",
                    borderRadius: 14, padding: "20px 22px", boxShadow: "0 16px 40px rgba(0,0,0,.45)",
                    maxWidth: 360, width: "calc(100% - 32px)",
                  }}>
                    <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                      <span style={{ flex: "none", color: "#fbbf24", fontSize: 18 }}>⚠</span>
                      <div>
                        <div style={{ font: "700 15px Outfit, sans-serif", color: "var(--text)", marginBottom: 4 }}>
                          Sign Out
                        </div>
                        <div style={{ font: "400 13px/1.5 Outfit, sans-serif", color: "var(--muted)" }}>
                          Are you sure you want to log out?
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                      <button
                        onClick={() => setLogoutOpen(false)}
                        style={{
                          height: 34, padding: "0 14px", borderRadius: 8, border: "1px solid var(--border)",
                          background: "var(--surface2)", color: "var(--muted)",
                          font: "600 13px Outfit, sans-serif", cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={confirmLogout}
                        disabled={loggingOut}
                        style={{
                          height: 34, padding: "0 14px", borderRadius: 8, border: "none",
                          background: "#f87171", color: "#fff",
                          font: "700 13px Outfit, sans-serif", cursor: "pointer",
                          opacity: loggingOut ? 0.6 : 1,
                        }}
                      >
                        {loggingOut ? "Logging out…" : "Logout"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </header>

          {/* Content */}
          <div style={{
            flex: 1, minHeight: 0, overflowY: "auto", padding: "14px 16px 28px", background: "var(--bg)",
          }}>
            {userLoaded ? <Outlet /> : <Loading layout="default" />}
          </div>
        </div>
      </div>

      <WhatsNewModal />
    </div>
  );
};

export default DefaultLayout;
